import { Flex, Typography } from "antd";
import styled from "styled-components";
import { CaretDownIcon } from "@phosphor-icons/react";
import { ReactNode, useState } from "react";
import { useTranslate } from "@refinedev/core";
import { Chapter } from "../../types";
import { ScenesAccordion } from "./ScenesAccordion";
import { LayersPanel } from "./LayersPanel";
import { Collapsible } from "./Collapsible";

const I18N = "components.templates.TemplatesBoard.storyboardLeftPanel";

const Container = styled(Flex)`
  height: 100%;
  overflow: hidden;
  background-color: ${({ theme }) => theme.colorBgContainer};
`;

// Matches the right-menu header tone.
const SectionHeader = styled(Flex)`
  cursor: pointer;
  user-select: none;
  padding: 12px;
  gap: 8px;
  align-items: center;
  background-color: ${({ theme }) => theme.colorBgContainerDisabled};
  border-block-end: 1px solid ${({ theme }) => theme.colorBorder};
`;

const Caret = styled(CaretDownIcon)<{ $open: boolean }>`
  transition: transform 0.2s ease;
  transform: rotate(${({ $open }) => ($open ? 0 : -90)}deg);
  flex-shrink: 0;
`;

const ScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;

  scrollbar-width: thin;
  scrollbar-color: ${({ theme }) => theme.colorBorder} transparent;
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.colorBorder};
    border-radius: 3px;
  }
`;

const SectionHeaderContent = ({
  label,
  open,
}: {
  label: string;
  open: boolean;
}) => (
  <>
    <Caret size={14} $open={open} />
    <Typography.Text strong style={{ textTransform: "uppercase", fontSize: 13 }}>
      {label}
    </Typography.Text>
  </>
);

export const StoryboardLeftPanel = ({
  handleSlideCardClick,
  handleChapterCardClick,
}: {
  handleSlideCardClick?: (uuid: string) => void;
  handleChapterCardClick?: (chapter: Chapter) => void;
}) => {
  const t = useTranslate();
  const [scenesOpen, setScenesOpen] = useState(true);
  const [layersOpen, setLayersOpen] = useState(true);

  const scenesBody: ReactNode = (
    <ScenesAccordion
      handleSlideCardClick={handleSlideCardClick}
      handleChapterCardClick={handleChapterCardClick}
    />
  );

  return (
    <Container vertical>
      {/* Scenes — grows to fill and scrolls on overflow. */}
      <Flex
        vertical
        style={{
          flex: scenesOpen ? "1 1 0" : "0 0 auto",
          minHeight: 0,
        }}
      >
        <SectionHeader onClick={() => setScenesOpen((v) => !v)}>
          <SectionHeaderContent label={t(`${I18N}.scenes`)} open={scenesOpen} />
        </SectionHeader>
        <Collapsible open={scenesOpen} fill>
          <ScrollArea>{scenesBody}</ScrollArea>
        </Collapsible>
      </Flex>

      {/* Layers — always visible; capped so it never hides the Scenes list. */}
      <Flex
        vertical
        style={{ flex: "0 1 auto", minHeight: 0, maxHeight: "50%" }}
      >
        <SectionHeader onClick={() => setLayersOpen((v) => !v)}>
          <SectionHeaderContent label={t(`${I18N}.layers`)} open={layersOpen} />
        </SectionHeader>
        <Collapsible open={layersOpen} fill>
          <ScrollArea>
            <LayersPanel />
          </ScrollArea>
        </Collapsible>
      </Flex>
    </Container>
  );
};
